import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { polygon } from "viem/chains";
import { decodeEventLog, formatEther, getAddress, isAddress, parseEther, type Address, type Hex } from "viem";
import { FileJson, FileUp, ImagePlus, Info, Loader2, ShoppingBag, Sparkles, Tag } from "lucide-react";
import Header from "@/components/Header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  NFT_COLLECTION_ABI, NFT_FACTORY_ABI, NFT_FACTORY_ADDRESS, NFT_LISTING_TYPES,
  NFT_MARKETPLACE_ABI, NFT_MARKETPLACE_ADDRESS,
} from "@/features/nft/config";

type ListingRow = {
  id: string; seller_address: Address; collection_address: Address; token_id: string;
  price_wei: string; nonce: string; deadline: string; signature: Hex; status: string;
};

const short = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`;
const errorMessage = (error: unknown) => error instanceof Error ? error.message.split("\n")[0] : "Transaction failed";

export default function NftStudio() {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  const [busy, setBusy] = useState<string>();
  const [createdCollection, setCreatedCollection] = useState<string>();
  const [collectionForm, setCollectionForm] = useState({ name: "", symbol: "", maxSupply: "100", baseURI: "ipfs://", royalty: "5" });
  const [mintForm, setMintForm] = useState({ collection: "", recipient: "", tokenURI: "ipfs://" });
  const [listingForm, setListingForm] = useState({ collection: "", tokenId: "1", price: "1", days: "7" });
  const [dropForm, setDropForm] = useState({ collection: "", price: "0.1", walletLimit: "5", quantity: "1" });
  const [mediaForm, setMediaForm] = useState({ name: "", description: "", file: undefined as File | undefined });

  useEffect(() => {
    if (address && !mintForm.recipient) setMintForm((form) => ({ ...form, recipient: address }));
  }, [address, mintForm.recipient]);

  const listings = useQuery({
    queryKey: ["nft-listings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("nft_listings").select("*")
        .eq("status", "active").gt("deadline", new Date().toISOString()).order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as ListingRow[];
    },
    retry: false,
  });

  const requireWallet = () => {
    if (!address || !walletClient || !publicClient) throw new Error("Connect a Polygon wallet first");
    return { address, walletClient, publicClient };
  };

  const createCollection = async () => {
    try {
      const wallet = requireWallet();
      if (!NFT_FACTORY_ADDRESS) throw new Error("VITE_NFT_FACTORY_ADDRESS is not configured");
      if (!collectionForm.name.trim() || !collectionForm.symbol.trim()) throw new Error("Name and symbol are required");
      const maxSupply = BigInt(collectionForm.maxSupply);
      const royaltyBps = Math.round(Number(collectionForm.royalty) * 100);
      if (maxSupply <= 0n || royaltyBps < 0 || royaltyBps > 2000) throw new Error("Use a positive supply and royalty between 0% and 20%");
      setBusy("create");
      const hash = await wallet.walletClient.writeContract({
        address: NFT_FACTORY_ADDRESS, abi: NFT_FACTORY_ABI, functionName: "createCollection",
        args: [collectionForm.name.trim(), collectionForm.symbol.trim().toUpperCase(), maxSupply, collectionForm.baseURI.trim(), wallet.address, BigInt(royaltyBps)],
        chain: polygon, account: wallet.address,
      });
      const receipt = await wallet.publicClient.waitForTransactionReceipt({ hash });
      let nextAddress: Address | undefined;
      for (const log of receipt.logs) {
        if (log.address.toLowerCase() !== NFT_FACTORY_ADDRESS.toLowerCase()) continue;
        try {
          const decoded = decodeEventLog({
            abi: NFT_FACTORY_ABI,
            data: log.data,
            topics: (log as unknown as { topics: [Hex, ...Hex[]] }).topics,
          }) as { eventName: "CollectionCreated"; args: { collection: Address } };
          if (decoded.eventName === "CollectionCreated") nextAddress = decoded.args.collection;
        } catch { /* another factory log */ }
      }
      setCreatedCollection(nextAddress);
      if (nextAddress) {
        setMintForm((form) => ({ ...form, collection: nextAddress }));
        setListingForm((form) => ({ ...form, collection: nextAddress }));
        setDropForm((form) => ({ ...form, collection: nextAddress }));
      }
      toast({ title: "Collection deployed", description: nextAddress || short(hash) });
    } catch (error) { toast({ variant: "destructive", title: "Could not create collection", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const uploadMedia = async () => {
    try {
      const wallet = requireWallet();
      const file = mediaForm.file;
      if (!file || !mediaForm.name.trim()) throw new Error("Choose a file and enter an NFT name");
      setBusy("upload");
      const timestamp = Date.now();
      const message = `Matic Moon NFT upload\nWallet: ${wallet.address}\nTimestamp: ${timestamp}\nFile: ${file.name}\nSize: ${file.size}`;
      const signature = await wallet.walletClient.signMessage({ account: wallet.address, message });
      const body = new FormData();
      body.append("file", file);
      body.append("name", mediaForm.name.trim());
      body.append("description", mediaForm.description.trim());
      body.append("address", wallet.address);
      body.append("timestamp", String(timestamp));
      body.append("signature", signature);
      const { data, error } = await supabase.functions.invoke<{ tokenUri: string }>("nft-media", { body });
      if (error) throw error;
      if (!data?.tokenUri) throw new Error("The pinning provider did not return a metadata URI");
      setMintForm((form) => ({ ...form, tokenURI: data.tokenUri }));
      toast({ title: "Media pinned to IPFS", description: data.tokenUri });
    } catch (error) { toast({ variant: "destructive", title: "Upload failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const mintNft = async () => {
    try {
      const wallet = requireWallet();
      if (!isAddress(mintForm.collection) || !isAddress(mintForm.recipient)) throw new Error("Enter valid collection and recipient addresses");
      setBusy("mint");
      const hash = await wallet.walletClient.writeContract({
        address: getAddress(mintForm.collection), abi: NFT_COLLECTION_ABI, functionName: "ownerMint",
        args: [getAddress(mintForm.recipient), mintForm.tokenURI.trim()], chain: polygon, account: wallet.address,
      });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      toast({ title: "NFT minted", description: `Transaction ${short(hash)}` });
    } catch (error) { toast({ variant: "destructive", title: "Mint failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const createListing = async () => {
    try {
      const wallet = requireWallet();
      if (!NFT_MARKETPLACE_ADDRESS) throw new Error("VITE_NFT_MARKETPLACE_ADDRESS is not configured");
      if (!isAddress(listingForm.collection)) throw new Error("Enter a valid collection address");
      const collection = getAddress(listingForm.collection);
      const tokenId = BigInt(listingForm.tokenId);
      const price = parseEther(listingForm.price);
      const owner = await wallet.publicClient.readContract({ address: collection, abi: NFT_COLLECTION_ABI, functionName: "ownerOf", args: [tokenId], authorizationList: undefined });
      if (owner.toLowerCase() !== wallet.address.toLowerCase()) throw new Error("The connected wallet does not own this NFT");
      setBusy("list");
      const approved = await wallet.publicClient.readContract({ address: collection, abi: NFT_COLLECTION_ABI, functionName: "getApproved", args: [tokenId], authorizationList: undefined });
      if (approved.toLowerCase() !== NFT_MARKETPLACE_ADDRESS.toLowerCase()) {
        const approvalHash = await wallet.walletClient.writeContract({ address: collection, abi: NFT_COLLECTION_ABI, functionName: "approve", args: [NFT_MARKETPLACE_ADDRESS, tokenId], chain: polygon, account: wallet.address });
        await wallet.publicClient.waitForTransactionReceipt({ hash: approvalHash });
      }
      const nonce = BigInt(Date.now());
      const deadline = BigInt(Math.floor(Date.now() / 1000) + Math.max(1, Number(listingForm.days)) * 86_400);
      const message = { seller: wallet.address, collection, tokenId, price, nonce, deadline } as const;
      const signature = await wallet.walletClient.signTypedData({
        account: wallet.address, domain: { name: "Matic Moon NFT Marketplace", version: "1", chainId: polygon.id, verifyingContract: NFT_MARKETPLACE_ADDRESS },
        types: NFT_LISTING_TYPES, primaryType: "Listing", message,
      });
      const { error } = await supabase.functions.invoke("nft-listings", { body: {
        seller: wallet.address, collection, tokenId: tokenId.toString(), price: price.toString(), nonce: nonce.toString(), deadline: deadline.toString(), signature,
      } });
      if (error) throw error;
      await listings.refetch();
      toast({ title: "NFT listed", description: `${listingForm.price} MATIC for ${listingForm.days} days` });
    } catch (error) { toast({ variant: "destructive", title: "Listing failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const buyListing = async (listing: ListingRow) => {
    try {
      const wallet = requireWallet();
      if (!NFT_MARKETPLACE_ADDRESS) throw new Error("Marketplace is not configured");
      setBusy(listing.id);
      const tuple = {
        seller: getAddress(listing.seller_address), collection: getAddress(listing.collection_address), tokenId: BigInt(listing.token_id),
        price: BigInt(listing.price_wei), nonce: BigInt(listing.nonce), deadline: BigInt(Math.floor(new Date(listing.deadline).getTime() / 1000)),
      } as const;
      const hash = await wallet.walletClient.writeContract({
        address: NFT_MARKETPLACE_ADDRESS, abi: NFT_MARKETPLACE_ABI, functionName: "buy", args: [tuple, listing.signature],
        value: tuple.price, chain: polygon, account: wallet.address,
      });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      const { error } = await supabase.functions.invoke("nft-listings", { body: { action: "reconcile", id: listing.id, transactionHash: hash } });
      if (error) throw error;
      toast({ title: "Purchase complete", description: `NFT ownership transferred in ${short(hash)}` });
      await listings.refetch();
    } catch (error) { toast({ variant: "destructive", title: "Purchase failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const cancelListing = async (listing: ListingRow) => {
    try {
      const wallet = requireWallet();
      if (!NFT_MARKETPLACE_ADDRESS) throw new Error("Marketplace is not configured");
      if (listing.seller_address.toLowerCase() !== wallet.address.toLowerCase()) throw new Error("Only the seller can cancel this listing");
      setBusy(listing.id);
      const hash = await wallet.walletClient.writeContract({
        address: NFT_MARKETPLACE_ADDRESS, abi: NFT_MARKETPLACE_ABI, functionName: "cancelListing",
        args: [BigInt(listing.nonce)], chain: polygon, account: wallet.address,
      });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      const { error } = await supabase.functions.invoke("nft-listings", { body: { action: "reconcile", id: listing.id, transactionHash: hash } });
      if (error) throw error;
      await listings.refetch();
      toast({ title: "Listing cancelled", description: short(hash) });
    } catch (error) { toast({ variant: "destructive", title: "Cancellation failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const configureDrop = async () => {
    try {
      const wallet = requireWallet();
      if (!isAddress(dropForm.collection)) throw new Error("Enter a valid collection address");
      const price = parseEther(dropForm.price);
      const walletLimit = BigInt(dropForm.walletLimit);
      setBusy("configure-drop");
      const hash = await wallet.walletClient.writeContract({
        address: getAddress(dropForm.collection), abi: NFT_COLLECTION_ABI, functionName: "configurePublicMint",
        args: [true, price, walletLimit], chain: polygon, account: wallet.address,
      });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      toast({ title: "Public mint opened", description: `${dropForm.price} MATIC per NFT` });
    } catch (error) { toast({ variant: "destructive", title: "Drop configuration failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const mintFromDrop = async () => {
    try {
      const wallet = requireWallet();
      if (!isAddress(dropForm.collection)) throw new Error("Enter a valid collection address");
      const collection = getAddress(dropForm.collection);
      const quantity = BigInt(dropForm.quantity);
      if (quantity <= 0n) throw new Error("Quantity must be positive");
      setBusy("public-mint");
      const price = await wallet.publicClient.readContract({ address: collection, abi: NFT_COLLECTION_ABI, functionName: "publicMintPrice", authorizationList: undefined });
      const hash = await wallet.walletClient.writeContract({
        address: collection, abi: NFT_COLLECTION_ABI, functionName: "publicMint", args: [quantity],
        value: price * quantity, chain: polygon, account: wallet.address,
      });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      toast({ title: "Public mint complete", description: `${quantity} NFT${quantity === 1n ? "" : "s"} minted` });
    } catch (error) { toast({ variant: "destructive", title: "Public mint failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const withdraw = async () => {
    try {
      const wallet = requireWallet();
      if (!NFT_MARKETPLACE_ADDRESS) throw new Error("Marketplace is not configured");
      setBusy("withdraw");
      const hash = await wallet.walletClient.writeContract({ address: NFT_MARKETPLACE_ADDRESS, abi: NFT_MARKETPLACE_ABI, functionName: "withdrawProceeds", chain: polygon, account: wallet.address });
      await wallet.publicClient.waitForTransactionReceipt({ hash });
      toast({ title: "Proceeds withdrawn", description: short(hash) });
    } catch (error) { toast({ variant: "destructive", title: "Withdrawal failed", description: errorMessage(error) }); }
    finally { setBusy(undefined); }
  };

  const notConfigured = !NFT_FACTORY_ADDRESS || !NFT_MARKETPLACE_ADDRESS;
  return <div className="min-h-screen bg-background">
    <Header />
    <main className="container mx-auto px-4 pb-16 pt-24">
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div><Badge variant="secondary" className="mb-3">Polygon · non-custodial</Badge><h1 className="text-4xl font-bold">NFT Studio</h1><p className="mt-2 max-w-2xl text-muted-foreground">Create creator-owned collections, mint IPFS-backed NFTs, and trade with wallet-signed listings.</p></div>
        <Button variant="outline" disabled={!isConnected || busy === "withdraw"} onClick={withdraw}>{busy === "withdraw" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Withdraw sale proceeds</Button>
      </div>
      {notConfigured && <Alert className="mb-6"><Sparkles className="h-4 w-4" /><AlertTitle>Deployment required</AlertTitle><AlertDescription>Deploy the NFT contracts and configure VITE_NFT_FACTORY_ADDRESS and VITE_NFT_MARKETPLACE_ADDRESS. The interface is ready but will not submit transactions until then.</AlertDescription></Alert>}
      <Card className="mb-6">
        <CardHeader><CardTitle>Pin NFT media</CardTitle><CardDescription>Upload media and metadata through the wallet-authorized server-side IPFS pinning function.</CardDescription></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="NFT name"><Input value={mediaForm.name} onChange={(e) => setMediaForm({ ...mediaForm, name: e.target.value })} /></Field>
          <Field label="Description"><Input value={mediaForm.description} onChange={(e) => setMediaForm({ ...mediaForm, description: e.target.value })} /></Field>
          <Field label="Media file"><Input type="file" accept="image/*,audio/*,video/*" onChange={(e) => setMediaForm({ ...mediaForm, file: e.target.files?.[0] })} /></Field>
          <div className="flex items-end"><Button className="w-full" variant="outline" disabled={!isConnected || !!busy || !mediaForm.file} onClick={uploadMedia}>{busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}Pin media & metadata</Button></div>
        </CardContent>
      </Card>
      <Tabs defaultValue="market">
        <TabsList className="mb-6"><TabsTrigger value="market"><ShoppingBag className="mr-2 h-4 w-4" />Marketplace</TabsTrigger><TabsTrigger value="create"><ImagePlus className="mr-2 h-4 w-4" />Create</TabsTrigger><TabsTrigger value="manage"><Tag className="mr-2 h-4 w-4" />Mint & list</TabsTrigger></TabsList>
        <TabsContent value="market">
          {listings.isLoading ? <div className="py-16 text-center text-muted-foreground">Loading listings…</div> : listings.isError ? <Alert><AlertTitle>Marketplace index unavailable</AlertTitle><AlertDescription>Apply the NFT Supabase migration to load signed listings.</AlertDescription></Alert> : listings.data?.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{listings.data.map((listing) => <Card key={listing.id} className="overflow-hidden"><div className="flex aspect-video items-center justify-center bg-gradient-to-br from-primary/20 via-accent/10 to-background"><ImagePlus className="h-12 w-12 text-primary/70" /></div><CardHeader><CardTitle>NFT #{listing.token_id}</CardTitle><CardDescription>{short(listing.collection_address)} · seller {short(listing.seller_address)}</CardDescription></CardHeader><CardContent><div className="mb-4 text-2xl font-semibold">{formatEther(BigInt(listing.price_wei))} MATIC</div><Button className="w-full" disabled={!isConnected || busy === listing.id || listing.seller_address.toLowerCase() === address?.toLowerCase()} onClick={() => buyListing(listing)}>{busy === listing.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Buy now</Button></CardContent></Card>)}</div> : <Card><CardContent className="py-16 text-center text-muted-foreground">No active listings yet. Mint an NFT and create the first listing.</CardContent></Card>}
        </TabsContent>
        <TabsContent value="create">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>Deploy an ERC-721 collection</CardTitle>
              <CardDescription>Your wallet becomes the collection owner. Royalties use EIP-2981 and are capped at 20%.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Field label="Collection name">
                <Input value={collectionForm.name} onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })} placeholder="Moon Artists" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Symbol">
                  <Input value={collectionForm.symbol} onChange={(e) => setCollectionForm({ ...collectionForm, symbol: e.target.value })} placeholder="MOON" />
                </Field>
                <Field label="Max supply">
                  <Input type="number" min="1" value={collectionForm.maxSupply} onChange={(e) => setCollectionForm({ ...collectionForm, maxSupply: e.target.value })} />
                </Field>
                <Field label="Royalty %">
                  <Input type="number" min="0" max="20" step="0.1" value={collectionForm.royalty} onChange={(e) => setCollectionForm({ ...collectionForm, royalty: e.target.value })} />
                </Field>
              </div>
              <BaseMetadataUriField
                value={collectionForm.baseURI}
                onChange={(baseURI) => setCollectionForm({ ...collectionForm, baseURI })}
              />
              {createdCollection && <Alert><AlertTitle>New collection</AlertTitle><AlertDescription className="break-all">{createdCollection}</AlertDescription></Alert>}
              <Button disabled={!isConnected || !!busy || !NFT_FACTORY_ADDRESS} onClick={createCollection}>
                {busy === "create" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Deploy collection
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="manage">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Mint an NFT</CardTitle>
                <CardDescription>Only the collection owner can use this action. The token URI should point to immutable IPFS JSON.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4">
                <Field label="Collection address">
                  <Input value={mintForm.collection} onChange={(e) => setMintForm({ ...mintForm, collection: e.target.value })} />
                </Field>
                <Field label="Recipient">
                  <Input value={mintForm.recipient} onChange={(e) => setMintForm({ ...mintForm, recipient: e.target.value })} />
                </Field>
                <TokenMetadataUriField
                  value={mintForm.tokenURI}
                  onChange={(tokenURI) => setMintForm({ ...mintForm, tokenURI })}
                />
                <Button disabled={!isConnected || !!busy} onClick={mintNft}>
                  {busy === "mint" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Mint NFT
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Create a fixed-price listing</CardTitle>
                <CardDescription>The NFT remains in your wallet. You approve the marketplace, then sign an off-chain EIP-712 listing.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4">
                <Field label="Collection address">
                  <Input value={listingForm.collection} onChange={(e) => setListingForm({ ...listingForm, collection: e.target.value })} />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Token ID"><Input type="number" min="1" value={listingForm.tokenId} onChange={(e) => setListingForm({ ...listingForm, tokenId: e.target.value })} /></Field>
                  <Field label="Price (MATIC)"><Input type="number" min="0" step="0.001" value={listingForm.price} onChange={(e) => setListingForm({ ...listingForm, price: e.target.value })} /></Field>
                  <Field label="Days"><Input type="number" min="1" value={listingForm.days} onChange={(e) => setListingForm({ ...listingForm, days: e.target.value })} /></Field>
                </div>
                <Button disabled={!isConnected || !!busy || !NFT_MARKETPLACE_ADDRESS} onClick={createListing}>
                  {busy === "list" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Approve &amp; sign listing
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card><CardHeader><CardTitle>Configure a public drop</CardTitle><CardDescription>Collection owners can open a fixed-price mint with a per-wallet limit.</CardDescription></CardHeader><CardContent className="grid gap-4"><Field label="Collection address"><Input value={dropForm.collection} onChange={(e) => setDropForm({ ...dropForm, collection: e.target.value })} /></Field><div className="grid grid-cols-2 gap-3"><Field label="Price (MATIC)"><Input type="number" min="0" step="0.001" value={dropForm.price} onChange={(e) => setDropForm({ ...dropForm, price: e.target.value })} /></Field><Field label="Wallet limit"><Input type="number" min="0" value={dropForm.walletLimit} onChange={(e) => setDropForm({ ...dropForm, walletLimit: e.target.value })} /></Field></div><Button disabled={!isConnected || !!busy} onClick={configureDrop}>{busy === "configure-drop" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Open public mint</Button></CardContent></Card>
        <Card><CardHeader><CardTitle>Mint from a public drop</CardTitle><CardDescription>The current on-chain price is read immediately before submitting payment.</CardDescription></CardHeader><CardContent className="grid gap-4"><Field label="Collection address"><Input value={dropForm.collection} onChange={(e) => setDropForm({ ...dropForm, collection: e.target.value })} /></Field><Field label="Quantity"><Input type="number" min="1" value={dropForm.quantity} onChange={(e) => setDropForm({ ...dropForm, quantity: e.target.value })} /></Field><Button disabled={!isConnected || !!busy} onClick={mintFromDrop}>{busy === "public-mint" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Mint from drop</Button></CardContent></Card>
      </div>
      {listings.data?.some((listing) => listing.seller_address.toLowerCase() === address?.toLowerCase()) && <Card className="mt-6"><CardHeader><CardTitle>Your active listings</CardTitle><CardDescription>Cancellation is recorded on Polygon before the Supabase read model is updated.</CardDescription></CardHeader><CardContent className="grid gap-3">{listings.data.filter((listing) => listing.seller_address.toLowerCase() === address?.toLowerCase()).map((listing) => <div key={listing.id} className="flex items-center justify-between gap-4 rounded-md border p-3"><span>NFT #{listing.token_id} · {formatEther(BigInt(listing.price_wei))} MATIC</span><Button variant="outline" disabled={busy === listing.id} onClick={() => cancelListing(listing)}>{busy === listing.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Cancel</Button></div>)}</CardContent></Card>}
    </main>
  </div>;
}

const metadataDocumentExample = `{
  "name": "Moon Artists #1",
  "description": "An original member of the Moon Artists collection.",
  "image": "ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3/image-1.png",
  "external_url": "https://maticmoon.com/nfts",
  "attributes": [
    { "trait_type": "Background", "value": "Lunar" },
    { "trait_type": "Edition", "value": 1 }
  ]
}`;

function TokenMetadataUriField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="token-metadata-uri">Token metadata URI</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="About the token metadata URI"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs leading-relaxed">
              The permanent IPFS address of one JSON metadata document for this NFT. It should describe the NFT and link to its IPFS-hosted media.
            </TooltipContent>
          </Tooltip>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button type="button" variant="link" size="sm" className="h-auto gap-1.5 p-0 text-xs">
              <FileJson className="h-3.5 w-3.5" />
              View example
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] w-[calc(100%-1rem)] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Token metadata URI example</DialogTitle>
              <DialogDescription>
                This field points directly to one JSON document for the NFT you are minting.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 text-sm">
              <div className="space-y-2">
                <p className="font-medium">Value entered in the form</p>
                <code className="block break-all rounded-md border bg-muted/40 p-3 text-xs">
                  ipfs://bafybeihashofyourmetadata/document.json
                </code>
                <p className="text-muted-foreground">
                  Upload the media first, place its <code>ipfs://</code> URI in the JSON document, then upload and pin the JSON file. Enter the JSON file&apos;s resulting URI here.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium">Contents of document.json</p>
                <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs leading-5">
                  <code>{metadataDocumentExample}</code>
                </pre>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>This is a file URI, not a folder URI</AlertTitle>
                <AlertDescription>
                  Owner minting stores this exact value for the NFT. Do not use the collection&apos;s base folder unless that URI itself resolves to a valid JSON document.
                </AlertDescription>
              </Alert>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Input
        id="token-metadata-uri"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="ipfs://bafy.../document.json"
        aria-describedby="token-metadata-uri-help"
      />
      <p id="token-metadata-uri-help" className="text-xs text-muted-foreground">
        Enter the permanent <code>ipfs://</code> URI of this NFT&apos;s JSON metadata document.
      </p>
    </div>
  );
}

function BaseMetadataUriField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="base-metadata-uri">Base metadata URI</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="About the base metadata URI"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs leading-relaxed">
              This is the IPFS folder containing one JSON metadata document per token. End it with a slash; token #1 resolves to the base URI plus 1.
            </TooltipContent>
          </Tooltip>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button type="button" variant="link" size="sm" className="h-auto gap-1.5 p-0 text-xs">
              <FileJson className="h-3.5 w-3.5" />
              View example
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] w-[calc(100%-1rem)] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Base metadata URI example</DialogTitle>
              <DialogDescription>
                Public mints append the token ID to this folder URI to find that NFT&apos;s JSON metadata.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 text-sm">
              <div className="space-y-2">
                <p className="font-medium">IPFS folder</p>
                <div className="rounded-md border bg-muted/40 p-3 font-mono text-xs leading-6">
                  <p>metadata/</p>
                  <p className="pl-4">├── 1</p>
                  <p className="pl-4">├── 2</p>
                  <p className="pl-4">└── 3</p>
                </div>
                <p className="text-muted-foreground">
                  After uploading that folder, enter a URI such as:
                </p>
                <code className="block break-all rounded-md border bg-muted/40 p-3 text-xs">
                  ipfs://bafybeihashofyourmetadatafolder/metadata/
                </code>
                <p className="text-muted-foreground">
                  Token #1 then resolves to <code className="break-all text-foreground">ipfs://bafybeihashofyourmetadatafolder/metadata/1</code>.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium">Contents of document 1</p>
                <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs leading-5">
                  <code>{metadataDocumentExample}</code>
                </pre>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Keep the files extensionless</AlertTitle>
                <AlertDescription>
                  For public mints, this collection contract requests 1, 2, 3, and so on—not 1.json. Each file should still contain valid JSON, and its image should use a permanent IPFS URI.
                </AlertDescription>
              </Alert>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Input
        id="base-metadata-uri"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="ipfs://bafy.../metadata/"
        aria-describedby="base-metadata-uri-help"
      />
      <p id="base-metadata-uri-help" className="text-xs text-muted-foreground">
        Use an IPFS folder URI ending in <code>/</code>. The contract appends each publicly minted token&apos;s ID.
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="grid gap-2"><Label>{label}</Label>{children}</div>;
}
